import { Injectable } from '@angular/core';
import cloneDeep from 'lodash.clonedeep';
import { v4 as uuid } from 'uuid';
import { combineLatest, mergeMap, Observable, of, take } from 'rxjs';

import {
  DailyStockDetails,
  DprActivitiesDurationHistory,
  DprApprover,
  DprConsumableDetail,
  DprDetails,
  DprHseDetail,
  DprPersonnelDetail,
  DprProgressDetail,
  DprProgressWithoutTargetDetail,
  DprStatusTrackerDetails,
  DprTableOfColumnDetail,
  DprTableOfColumns,
  ProgressStockDetails,
  WorkedHoursDetails,
} from '@shared/models/dpr';
import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import { ProjectSetup } from '@shared/models';
import {
  addDaysToDate,
  computeDiffTimeAsMinutes,
  convertTimeToDate,
  toUTCDate,
} from '@shared/utils';
import {
  DailyStockService,
  ProgressStockService,
  WorkedHoursService,
} from '..';
import {
  ProgressItem,
  ProgressWithoutTargetItem,
  StatusTrackerWithData,
} from '@shared/models/project-setup';
import { UtcDateTimeOptions } from '@shared/enums';
import {
  findPreviousDprDetails,
  getRowAnchorValue,
  getStatusTrackerCellId,
  withoutDerivedStatusTrackerCells,
} from '@dpr-details/utils';
import { DprAccumulationOfflineService } from '.';

@Injectable()
export class CreateDprDetailsOfflineService {
  constructor(
    private offlineFacade: OfflineFacade,
    private userDetailsFacade: UserDetailsFacade,
    private dailyStockService: DailyStockService,
    private workedHoursService: WorkedHoursService,
    private progressStockService: ProgressStockService,
    private dprAccumulationOfflineService: DprAccumulationOfflineService
  ) {}

  createDprDetails(subprojectId: number, date: Date): Observable<DprDetails> {
    let dprDate =
      toUTCDate(date, UtcDateTimeOptions.UtcDateWithoutTimeZone) ?? new Date();
    return combineLatest([
      this.userDetailsFacade.query.projectSetup$,
      this.offlineFacade.query.combinedDprDetailsList$,
    ]).pipe(
      take(1),
      mergeMap(([projectSetup, combinedDprDetailsList]) => {
        let displayId = 1;
        let approvers: DprApprover[] = [];
        const lastDprDetails = combinedDprDetailsList
          ? combinedDprDetailsList[0]
          : null;
        const statusTrackerSubTabsToCopy: number[] = projectSetup.statusTrackers
          .filter((x) => x.isActive && x.autoCopy)
          .map((y) => +y.id);

        if (lastDprDetails) {
          displayId = lastDprDetails.dprDisplayId + 1;
          if (!projectSetup.subprojectConfiguration.enableCustomDates) {
            dprDate = addDaysToDate(
              toUTCDate(
                new Date(lastDprDetails.dprDateTime),
                UtcDateTimeOptions.UtcDateWithoutTimeZone
              ),
              1
            );
          }
        }
        // Everything the new DPR copies comes from the DPR before it. Without
        // custom dates the new DPR follows the last one, which west of UTC can
        // share its date, so the last DPR is taken rather than looked up by date.
        // The offline date picker only offers dates after the last DPR, so the
        // lookup guards a date between two DPRs.
        const previousDprDetails = projectSetup.subprojectConfiguration
          .enableCustomDates
          ? findPreviousDprDetails(combinedDprDetailsList, dprDate)
          : lastDprDetails;
        if (previousDprDetails) {
          approvers = cloneDeep(
            previousDprDetails.dprApprovers.filter((a) =>
              projectSetup.approverList.some((aa) => aa.id === a.approverId)
            )
          );
        }
        const dprDetailId = uuid();
        const dprHseId = uuid();
        const dprPersonnelId = uuid();
        const dprConsumableId = uuid();
        const dprProgressId = uuid();
        const dprProgressWithoutTargetId = uuid();
        const dprStatusTrackerId = uuid();
        approvers.forEach((a) => {
          a.id = uuid();
          a.dprDetailId = dprDetailId;
        });
        const dprDetails: DprDetails = {
          id: dprDetailId,
          dprId: uuid(),
          dprDisplayId: displayId,
          dprSubprojectId: subprojectId,
          dprDateTime: dprDate,
          revision: 1,
          firstWarning: 0,
          lockedDate: null,
          isSignDprDetailsUploaded: false,
          dprApprovers: approvers,
          dprActivities: [],
          dprParallelActivities: [],
          dprStatusTracker: {
            id: dprStatusTrackerId,
            dprDetailId,
            dprStatusTrackerDetails: this.getStatusTrackerDetails(
              dprDetailId,
              dprStatusTrackerId,
              statusTrackerSubTabsToCopy,
              previousDprDetails,
              projectSetup.statusTrackers
            ),
          },
          dprActivitiesDurationHistory:
            this.getActivitiesHistory(previousDprDetails),
          dprWeather: {
            id: uuid(),
            comment: null,
            dprDetailId,
            dprWeatherDetails: [],
            dprWeatherAttachments: [],
          },
          dprHSE: {
            id: dprHseId,
            comment: null,
            dprDetailId,
            dprhseDetails: this.getHseDetails(
              dprHseId,
              projectSetup,
              previousDprDetails
            ),
            dprhseAttachments: [],
          },
          dprPersonnel: {
            id: dprPersonnelId,
            comment: null,
            dprDetailId,
            dprPersonnelDetails: this.getPersonnelDetails(
              dprPersonnelId,
              projectSetup,
              previousDprDetails
            ),
            dprPersonnelAttachments: [],
          },
          dprConsumable: {
            id: dprConsumableId,
            comment: null,
            dprDetailId,
            dprConsumableDetails: this.getConsumableDetails(
              dprConsumableId,
              projectSetup,
              previousDprDetails
            ),
            dprConsumableAttachments: [],
          },
          dprSummary: {
            id: uuid(),
            currentComment: null,
            nextComment: null,
            externalComment: null,
            dprDetailId,
            dprSummaryAttachments: [],
          },
          dprForecastActivities: [],
          dprAppendixAttachments: [],
          dprProgress: {
            id: dprProgressId,
            comment: null,
            dprDetailId,
            dprProgressDetails: this.getProgressDetails(
              dprProgressId,
              projectSetup,
              previousDprDetails
            ),
            dprProgressAttachments: [],
          },
          dprProgressWithoutTarget: {
            id: dprProgressWithoutTargetId,
            comment: null,
            dprDetailId,
            dprProgressWithoutTargetDetails:
              this.getProgressWithoutTargetDetails(
                dprProgressWithoutTargetId,
                projectSetup,
                previousDprDetails
              ),
            dprProgressWithoutTargetAttachments: [],
          },
          // Built before withPreviousValues runs below, so the carry-over starting
          // values land on these copied tables rather than on a second, empty entry
          // for the same table.
          dprTableOfColumns: this.getAnchoredTablesOfColumns(
            dprDetailId,
            projectSetup,
            previousDprDetails
          ),
          dprHidden: [],
          dprOnlineApprovals: [],
          dprFreeTextFields: [],
        };

        return of(
          this.dprAccumulationOfflineService.withPreviousValues(
            projectSetup,
            dprDetails,
            previousDprDetails ?? null
          )
        );
      })
    );
  }

  getPersonnelDetails(
    dprPersonnelId: number | string,
    projectSetup: ProjectSetup,
    lastDprDetails: DprDetails
  ): DprPersonnelDetail[] {
    const result: DprPersonnelDetail[] = [];
    projectSetup.personnelList.forEach((personnel) => {
      const dprPersonnelDetail: DprPersonnelDetail = {
        id: uuid(),
        dprPersonnelId,
        personnelId: personnel.id,
        workedHours: this.getPersonnelWorkedHours(personnel.id, lastDprDetails),
        previous: this.computePrevPersonnelValue(personnel.id, lastDprDetails),
        dailyStockDetails: this.computePersonnelDailyStockDetails(
          personnel.id,
          lastDprDetails
        ),
        comment: null,
      };
      dprPersonnelDetail.workedHoursDetails =
        this.computePersonnelWorkedHoursDetails(
          dprPersonnelDetail,
          lastDprDetails,
          projectSetup
        );
      result.push(dprPersonnelDetail);
    });
    return result;
  }

  computeConsumablesDailyStockDetails(
    consumableId: number,
    lastDprDetails: DprDetails
  ): DailyStockDetails {
    let dailyStockDetails = null;
    if (lastDprDetails?.dprConsumable?.dprConsumableDetails) {
      const dprConsumableDetail = this.findDprConsumableDetailByConsumableId(
        lastDprDetails.dprConsumable.dprConsumableDetails,
        consumableId
      );
      if (dprConsumableDetail) {
        dailyStockDetails = this.dailyStockService.onPreviousDayChanged(
          dprConsumableDetail.dailyStockDetails
        );
      }
    }
    return dailyStockDetails;
  }

  //#region Private methods

  private getActivitiesHistory(
    lastDprDetails: DprDetails
  ): DprActivitiesDurationHistory[] {
    if (!lastDprDetails) {
      return [];
    }
    const dprActivitiesHistory = cloneDeep(
      lastDprDetails.dprActivitiesDurationHistory
    ) as DprActivitiesDurationHistory[];

    // add primary activities durations
    for (const activity of lastDprDetails.dprActivities) {
      const history = dprActivitiesHistory.find(
        (x) =>
          x.activityId === activity.activityId &&
          x.activityGroupId === activity.activityGroupId &&
          x.parallelActivityType === 0
      );
      const timeDiff = computeDiffTimeAsMinutes(
        convertTimeToDate(activity.finish),
        convertTimeToDate(activity.start)
      );

      if (history) {
        history.totalDuration += timeDiff;
      } else {
        dprActivitiesHistory.push({
          activityId: activity.activityId,
          activityGroupId: activity.activityGroupId,
          parallelActivityType: 0,
          totalDuration: timeDiff,
        });
      }
    }
    // add parallel activity durations
    for (const activity of lastDprDetails.dprParallelActivities) {
      const history = dprActivitiesHistory.find(
        (x) =>
          x.activityId === activity.activityId &&
          x.activityGroupId === activity.activityGroupId &&
          x.parallelActivityType === activity.parallelActivityType
      );
      const timeDiff = computeDiffTimeAsMinutes(
        convertTimeToDate(activity.finish),
        convertTimeToDate(activity.start)
      );
      if (history) {
        history.totalDuration += timeDiff;
      } else {
        dprActivitiesHistory.push({
          activityId: activity.activityId,
          activityGroupId: activity.activityGroupId,
          parallelActivityType: activity.parallelActivityType,
          totalDuration: timeDiff,
        });
      }
    }

    return dprActivitiesHistory;
  }

  private getHseDetails(
    dprHseId: number | string,
    projectSetup: ProjectSetup,
    lastDprDetails: DprDetails
  ): DprHseDetail[] {
    const result: DprHseDetail[] = [];
    projectSetup.hseList.forEach((hse) => {
      result.push({
        id: null,
        dprhseId: dprHseId,
        hseId: hse.id,
        previous: this.computePrevHseValue(hse.id, lastDprDetails),
        comment: null,
      });
    });
    return result;
  }

  /**
   * Copied cells take the id they are queued under, so an offline save drops
   * them like any other unsaved cell.
   */
  private getStatusTrackerDetails(
    dprDetailId: string,
    dprStatusTrackerId: number | string,
    statusTrackerSubTabsToCopy: number[],
    lastDprDetails: DprDetails,
    statusTrackers: StatusTrackerWithData[]
  ): DprStatusTrackerDetails[] {
    const result: DprStatusTrackerDetails[] = [];
    withoutDerivedStatusTrackerCells(
      lastDprDetails?.dprStatusTracker?.dprStatusTrackerDetails,
      statusTrackers
    ).forEach((st) => {
      if (statusTrackerSubTabsToCopy.includes(st.statusTrackerId)) {
        result.push({
          id: getStatusTrackerCellId(
            dprDetailId,
            st.statusTrackerId,
            st.rowId,
            st.columnId
          ),
          dprStatusTrackerId,
          columnId: st.columnId,
          rowId: st.rowId,
          colorTypeId: st.colorTypeId,
          value: st.value,
          statusTrackerId: st.statusTrackerId,
        });
      }
    });
    return result;
  }

  /**
   * When a DPR is created online, the API copies the anchor column values of the
   * previous DPR into it. It copies nothing into a DPR created offline, so the copy
   * is made here.
   *
   * For each table with an active anchor column, every row of the previous DPR that
   * has an anchor option gives one new row holding only that option. The other cells
   * start empty, and the carry-over and formula cells are worked out when the DPR is
   * opened. The table's own active flag is not checked, so a switched-off table still
   * has its rows when it is switched back on. A table gets an entry only if at least
   * one row is copied.
   */
  private getAnchoredTablesOfColumns(
    dprDetailId: string,
    projectSetup: ProjectSetup,
    lastDprDetails: DprDetails
  ): DprTableOfColumns[] {
    return (projectSetup.tableOfColumns ?? []).flatMap((tableOfColumn) => {
      const anchorColumnId = tableOfColumn.anchorColumn?.anchorColumnId;
      const anchorItem =
        anchorColumnId == null
          ? undefined
          : tableOfColumn.tableOfColumnItems?.find(
              (item) => +item.id === +anchorColumnId
            );

      // No anchor column, or one switched off in the project setup: nothing to copy.
      if (!anchorItem || anchorItem.isActive === false) {
        return [];
      }

      // The option each row of the previous DPR holds. A row with an empty anchor
      // cell has nothing to copy.
      const anchorValues = (
        lastDprDetails?.dprTableOfColumns?.find(
          (x) => +x.tableOfColumnId === +tableOfColumn.id
        )?.dprTableOfColumnsDetails ?? []
      )
        .map((detail) => getRowAnchorValue(detail, +anchorItem.id))
        .filter((anchorValue) => anchorValue !== null);

      if (!anchorValues.length) {
        return [];
      }

      const dprTableOfColumnId = uuid();

      return [
        {
          id: dprTableOfColumnId,
          comment: null,
          tableOfColumnId: +tableOfColumn.id,
          dprDetailId,
          dprTableOfColumnsDetails: anchorValues.map((anchorValue) =>
            this.createAnchorRow(
              dprTableOfColumnId,
              +anchorItem.id,
              anchorValue
            )
          ),
          dprTableOfColumnsAttachment: [],
          syncDprTableOfColumnsDetails: null,
          syncDprTableOfColumnsAttachments: null,
        },
      ];
    });
  }

  /**
   * The row is linked to its new table through dprTableOfColumnId. The grid treats a
   * linked row as saved, so a copied row can't be deleted, the same as a row the API
   * copies into a DPR created online.
   */
  private createAnchorRow(
    dprTableOfColumnId: string,
    anchorColumnId: number,
    anchorValue: string
  ): DprTableOfColumnDetail {
    const id = uuid();

    return {
      id,
      dprTableOfColumnId,
      dprTableOfColumnDetailItems: [
        {
          id: uuid(),
          dprTableOfColumnDetailId: id,
          tableOfColumnItemId: anchorColumnId,
          value: anchorValue,
        },
      ],
    };
  }

  private computePrevHseValue(
    hseId: number,
    lastDprDetails: DprDetails
  ): number {
    let prev = 0;
    if (lastDprDetails?.dprHSE?.dprhseDetails) {
      const dprHseDetail = (lastDprDetails.dprHSE.dprhseDetails || []).find(
        (x) => x.hseId === hseId
      );
      if (dprHseDetail) {
        prev = dprHseDetail.previous;
        if (dprHseDetail.today) {
          prev += dprHseDetail.today;
        }
      }
    }
    return prev;
  }

  private getPersonnelWorkedHours(
    personnelId: number,
    lastDprDetails: DprDetails
  ): number {
    const dprPersonnelDetail = this.findDprPersonnelDetailByPersonnelId(
      lastDprDetails?.dprPersonnel?.dprPersonnelDetails,
      personnelId
    );

    return dprPersonnelDetail?.workedHours ?? null;
  }

  private computePrevPersonnelValue(
    personnelId: number,
    lastDprDetails: DprDetails
  ): number {
    let prev = 0;
    if (lastDprDetails?.dprPersonnel?.dprPersonnelDetails) {
      const dprPersonnelDetail = this.findDprPersonnelDetailByPersonnelId(
        lastDprDetails.dprPersonnel.dprPersonnelDetails,
        personnelId
      );
      if (dprPersonnelDetail) {
        prev = dprPersonnelDetail.previous;
        if (dprPersonnelDetail.today) {
          prev += dprPersonnelDetail.today;
        }
      }
    }
    return prev;
  }
  private computePersonnelDailyStockDetails(
    personnelId: number,
    lastDprDetails: DprDetails
  ): DailyStockDetails {
    let dailyStockDetails = null;
    if (lastDprDetails?.dprPersonnel?.dprPersonnelDetails) {
      const dprPersonnelDetail = this.findDprPersonnelDetailByPersonnelId(
        lastDprDetails.dprPersonnel.dprPersonnelDetails,
        personnelId
      );
      if (dprPersonnelDetail) {
        dailyStockDetails = this.dailyStockService.onPreviousDayChanged(
          dprPersonnelDetail.dailyStockDetails
        );
      }
    }
    return dailyStockDetails;
  }
  private computePersonnelWorkedHoursDetails(
    dprPersonnelDetail: DprPersonnelDetail,
    lastDprDetails: DprDetails,
    projectSetup: ProjectSetup
  ): WorkedHoursDetails {
    let workedHoursDetails = null;
    if (lastDprDetails?.dprPersonnel?.dprPersonnelDetails) {
      const lastDprPersonnelDetail = this.findDprPersonnelDetailByPersonnelId(
        lastDprDetails.dprPersonnel.dprPersonnelDetails,
        dprPersonnelDetail.personnelId
      );
      if (lastDprPersonnelDetail) {
        workedHoursDetails = this.workedHoursService.onPreviousDayChanged(
          lastDprPersonnelDetail,
          dprPersonnelDetail,
          projectSetup
        );
      }
    }
    return workedHoursDetails;
  }
  private findDprPersonnelDetailByPersonnelId(
    dprPersonnelDetails: DprPersonnelDetail[],
    personnelId: number
  ): DprPersonnelDetail {
    return (dprPersonnelDetails || []).find(
      (x) => x.personnelId === personnelId
    );
  }
  private getConsumableDetails(
    dprConsumableId: number | string,
    projectSetup: ProjectSetup,
    lastDprDetails: DprDetails
  ): DprConsumableDetail[] {
    const result: DprConsumableDetail[] = [];
    projectSetup.consumableList.forEach((consumable) => {
      result.push({
        id: null,
        dprConsumableId,
        consumableId: consumable.id,
        previous: this.computePrevConsumableValue(
          consumable.id,
          lastDprDetails
        ),
        dailyStockDetails: this.computeConsumablesDailyStockDetails(
          consumable.id,
          lastDprDetails
        ),
        comment: null,
      });
    });
    return result;
  }
  private computePrevConsumableValue(
    consumableId: number,
    lastDprDetails: DprDetails
  ): number {
    let prev = 0;
    if (lastDprDetails?.dprConsumable?.dprConsumableDetails) {
      const dprConsumableDetail = this.findDprConsumableDetailByConsumableId(
        lastDprDetails.dprConsumable.dprConsumableDetails,
        consumableId
      );
      if (dprConsumableDetail) {
        prev = dprConsumableDetail.previous;
        if (dprConsumableDetail.today) {
          prev += dprConsumableDetail.today;
        }
      }
    }
    return prev;
  }

  private findDprConsumableDetailByConsumableId(
    dprConsumableDetails: DprConsumableDetail[],
    consumableId: number
  ): DprConsumableDetail {
    return (dprConsumableDetails || []).find(
      (x) => x.consumableId === consumableId
    );
  }
  private getProgressDetails(
    dprProgressId: number | string,
    projectSetup: ProjectSetup,
    lastDprDetails: DprDetails
  ): DprProgressDetail[] {
    const result: DprProgressDetail[] = [];
    projectSetup.progress.progressItems.forEach((progress) => {
      result.push({
        id: null,
        dprProgressId,
        progressId: progress.id,
        progressStockDetails: this.computeProgressStockDetails(
          progress,
          lastDprDetails
        ),
        comment: null,
      });
    });
    return result;
  }

  private computeProgressStockDetails(
    progress: ProgressItem,
    lastDprDetails: DprDetails
  ): ProgressStockDetails {
    let progressStockDetails = null;
    if (lastDprDetails?.dprProgress?.dprProgressDetails) {
      const dprProgressDetail = (
        lastDprDetails.dprProgress.dprProgressDetails || []
      ).find((x) => x.progressId === progress.id);
      if (dprProgressDetail) {
        progressStockDetails = this.progressStockService.onPreviousDayChanged(
          dprProgressDetail.progressStockDetails,
          progress.initialValue
        );
      }
    }
    return progressStockDetails;
  }

  private getProgressWithoutTargetDetails(
    dprProgressWithoutTargetId: number | string,
    projectSetup: ProjectSetup,
    lastDprDetails
  ): DprProgressWithoutTargetDetail[] {
    const result: DprProgressWithoutTargetDetail[] = [];
    projectSetup.progressWithoutTarget.progressWithoutTargetItems.forEach(
      (progressWithoutTarget) => {
        result.push({
          id: null,
          dprProgressWithoutTargetId,
          progressWithoutTargetId: progressWithoutTarget.id,
          clearedTotal: this.computePrevProgressWithoutTargetValue(
            progressWithoutTarget,
            lastDprDetails
          ),
          comment: null,
        });
      }
    );
    return result;
  }

  private computePrevProgressWithoutTargetValue(
    progressWithoutTarget: ProgressWithoutTargetItem,
    lastDprDetails: DprDetails
  ): number {
    let clearedTotal = null;
    if (
      lastDprDetails?.dprProgressWithoutTarget?.dprProgressWithoutTargetDetails
    ) {
      const dprProgressWithoutTargetDetail = (
        lastDprDetails.dprProgressWithoutTarget
          .dprProgressWithoutTargetDetails || []
      ).find((x) => x.progressWithoutTargetId === progressWithoutTarget.id);
      if (dprProgressWithoutTargetDetail) {
        clearedTotal = dprProgressWithoutTargetDetail.clearedTotal;
      }
    }
    return clearedTotal;
  }

  //#endregion
}
